import * as go from 'gojs';
import { environment } from '@examdojo/core/environment';

go.Diagram.licenseKey = environment.gojs.licenseKey;

export function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/** Replace common LaTeX commands with Unicode characters. */
export function replaceLatexCommands(s: string): string {
  return (
    s
      .replace(/\\cap/g, '∩')
      .replace(/\\cup/g, '∪')
      .replace(/\\emptyset/g, '∅')
      .replace(/\\prime/g, '′')
      .replace(/\\ldots/g, '…')
      .replace(/\\pi/g, 'π')
      // \sqrt{x} → √x
      .replace(/\\sqrt\{([^}]*)}/g, '√$1')
      // \frac{a}{b} → a⁄b (fraction slash)
      .replace(/\\frac\{([^}]*)}\{([^}]*)}/g, '$1⁄$2')
      // \dfrac{a}{b} → a⁄b
      .replace(/\\dfrac\{([^}]*)}\{([^}]*)}/g, '$1⁄$2')
  );
}

/** Parse simple LaTeX into display text and optional color for GoJS TextBlock rendering. */
export function parseSimpleLatex(latex: string): { text: string; color?: string } {
  const trimmed = latex.trim();

  // Single colored group: {\color{#hex}text} — text may contain LaTeX commands
  const singleColor = /^\{\\color\{(#[0-9a-fA-F]{6})\}([\s\S]*)\}$/.exec(trimmed);
  if (singleColor) {
    return { text: replaceLatexCommands(singleColor[2]).trim(), color: singleColor[1] };
  }

  // Replace LaTeX commands with Unicode, strip color commands
  const text = replaceLatexCommands(trimmed)
    .replace(/\{\\color\{#[0-9a-fA-F]{6}\}([^}]*)\}/g, '$1')
    .replace(/[{}]/g, '')
    .trim();

  return { text };
}

export interface LatexSegment {
  text: string;
  color?: string;
  font?: string;
}

/** Parse LaTeX into colored segments for multi-color GoJS rendering. */
export function parseSimpleLatexSegments(latex: string): { text: string; color?: string; segments: LatexSegment[] } {
  const trimmed = latex.trim();
  const segments: LatexSegment[] = [];
  const regex = /\{\\color\{(#[0-9a-fA-F]{6})\}([^}]*)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(trimmed)) !== null) {
    // Text before this color group
    if (match.index > lastIndex) {
      const before = replaceLatexCommands(trimmed.slice(lastIndex, match.index)).replace(/[{}]/g, '');
      if (before.trim()) {
        segments.push({ text: before });
      }
    }
    segments.push({ text: replaceLatexCommands(match[2]).trim(), color: match[1] });
    lastIndex = match.index + match[0].length;
  }

  // Remaining text after last color group
  if (lastIndex < trimmed.length) {
    const remaining = replaceLatexCommands(trimmed.slice(lastIndex)).replace(/[{}]/g, '');
    if (remaining.trim()) {
      segments.push({ text: remaining });
    }
  }

  // No color groups found — treat whole string as one segment
  if (segments.length === 0) {
    segments.push({ text: replaceLatexCommands(trimmed).replace(/[{}]/g, '').trim() });
  }

  const fullText = segments.map((s) => s.text).join('');
  const singleColor = segments.length === 1 ? segments[0].color : undefined;

  return { text: fullText, color: singleColor, segments };
}

export function createGojsLayout(layoutType: string): go.Layout {
  const $ = go.GraphObject.make;

  switch (layoutType) {
    case 'tree':
      return $(go.TreeLayout, {
        angle: 90,
        layerSpacing: 40,
        nodeSpacing: 30,
        arrangement: go.TreeArrangement.Horizontal,
      });
    case 'force':
      return $(go.ForceDirectedLayout, {
        maxIterations: 200,
        defaultSpringLength: 50,
      });
    case 'layered':
      return $(go.LayeredDigraphLayout, {
        direction: 90,
        layerSpacing: 40,
        columnSpacing: 30,
      });
    case 'grid':
      return $(go.GridLayout, {
        wrappingColumn: 4,
        spacing: new go.Size(20, 20),
      });
    case 'none':
      // No auto-layout — nodes stay at their `loc` positions
      return $(go.Layout);
    default:
      return $(go.TreeLayout, {
        angle: 90,
        layerSpacing: 40,
        nodeSpacing: 30,
      });
  }
}
